import { cva } from 'class-variance-authority';

/**
 * Styles for the base-ui-backed Select primitive.
 *
 * The trigger is a real `<button>`, so it carries `cursor-pointer` and a
 * flex layout that reserves room for the value text and the chevron. The
 * popup is positioned via `Select.Positioner` (which handles anchor
 * placement) and rendered through `Select.Portal` so it escapes any
 * `overflow: hidden` ancestor.
 */

/** The trigger button. Sized to match the input control. */
export const triggerStyles = cva(
  [
    'inline-flex w-full cursor-pointer items-center justify-between gap-2',
    'rounded-control border bg-surface-raised text-content outline-none',
    'transition-colors',
    'focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-accent',
    'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-content-muted',
    'data-[placeholder]:text-content-muted',
  ],
  {
    variants: {
      size: {
        sm: 'h-8 px-2.5 text-xs',
        md: 'h-9 px-3 text-sm',
      },
      invalid: {
        true: 'border-critical',
        false: 'border-border hover:border-border-strong',
      },
    },
    defaultVariants: {
      size: 'md',
      invalid: false,
    },
  },
);

/**
 * Legacy export — kept so any code that still imports `controlStyles` does
 * not break. New code should use `triggerStyles`.
 */
export const controlStyles = triggerStyles;

/** Wraps the control so hint and error text align beneath it. */
export const fieldStyles = {
  root: 'flex w-full flex-col gap-1.5',
  control: 'relative w-full',
  label: 'text-sm font-medium text-content',
  hint: 'text-xs text-content-muted',
  error: 'text-xs text-critical',
  visuallyHidden: 'sr-only',
} as const;

/** Decorative chevron inside the trigger. `pointer-events-none` is implicit in base-ui. */
export const chevronStyles =
  'inline-flex size-4 shrink-0 items-center justify-center text-content-subtle';

/** The popup container — positioned by the Positioner, portaled to the body. */
export const popupStyles = {
  positioner: 'z-50 outline-none',
  popup:
    'min-w-(--anchor-width) overflow-hidden rounded-surface border border-border bg-surface-raised p-1 shadow-lg',
  list: 'max-h-72 overflow-y-auto outline-none',
} as const;

/** One option in the popup. */
export const itemStyles = {
  // The checkmark is absolutely positioned, so the item does not need to
  // reserve a left gutter for it. `pl-2.5` matches the trigger's horizontal
  // padding so the option text lines up with the trigger's value text.
  item:
    'relative flex cursor-pointer items-center gap-2 rounded-control py-1.5 pl-2.5 pr-2 text-sm text-content outline-none transition-colors data-[highlighted]:bg-surface-sunken data-[selected]:font-medium data-[disabled]:cursor-not-allowed data-[disabled]:text-content-muted data-[disabled]:data-[highlighted]:bg-transparent',
  indicator:
    'absolute left-2 inline-flex size-4 shrink-0 items-center justify-center text-accent',
  text: 'block truncate',
} as const;
